export const isNullish = <T>(value: T | undefined): value is Exclude<typeof value, T> => value === undefined || value === null

export function numberToStringFormat(value: number | string | undefined | null, divider: number = 1, decimal: boolean = false) {
  return Number((Number(value) / divider).toFixed(1)).toLocaleString('en-US', decimal ? {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  } : {
    maximumFractionDigits: 0,
  })
}

const formatNumber = (unformatted: number | string | undefined | null, short = true, decimal: boolean = false, fullTruncate: boolean = false, fullName: boolean = false, floorAboveThreshold: boolean = false) => {
  if (!unformatted || isNullish(unformatted) || Number(unformatted) < 0) {
    return '0'
  }
  const x = typeof unformatted === 'string' ? Number(unformatted) : unformatted
  const formatted = decimal ? x.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) : Math.floor(x).toLocaleString('en-US')
  if (!short) return formatted

  if (x >= 1e33) {
    return numberToStringFormat(x, 1e33, decimal) + (fullName ? ' Decillion' : 'Dc')
  }
  if (x >= 1e30) {
    return numberToStringFormat(x, 1e30, decimal) + (fullName ? ' Nonillion' : 'No')
  }
  if (x >= 1e27) {
    return numberToStringFormat(x, 1e27, decimal) + (fullName ? ' Octillion' : 'Oc')
  }
  if (x >= 1e24) {
    return numberToStringFormat(x, 1e24, decimal) + (fullName ? ' Septillion' : 'Sp')
  }
  if (x >= 1e21) {
    return numberToStringFormat(x, 1e21, decimal) + (fullName ? ' Sextillion' : 'Sx')
  }
  if (x >= 1e18) {
    return numberToStringFormat(x, 1e18, decimal) + (fullName ? ' Quintillion' : 'Qi')
  }
  if (x >= 1e15) {
    return numberToStringFormat(x, 1e15, decimal) + (fullName ? ' Quadrillion' : 'Qa')
  }
  if (x >= 1e12) {
    return numberToStringFormat(x, 1e12, decimal) + (fullName ? ' Trillion' : 'T')
  }
  if (x >= 1e9) {
    if (fullTruncate && floorAboveThreshold) {
      return Math.floor(Number(x) / 1e9) + (fullName ? ' Billion' : 'B')
    }
    return Number((Number(x) / 1e9).toFixed(1)) + (fullName ? ' Billion' : 'B')
  }
  if (x < 10_000_000 && !fullTruncate) {
    return numberToStringFormat(x, 1, decimal)
  }
  if (x >= 1e6) {
    if (fullTruncate && floorAboveThreshold) {
      return Math.floor(Number(x) / 1e6) + 'M'
    }
    return Number((Number(x) / 1e6).toFixed(1)) + 'M'
  }
  if (x >= 1000 && short) {
    if (fullTruncate && floorAboveThreshold) {
      return Math.floor(Number(x) / 1000) + 'K'
    }
    return Number((Number(x) / 1000).toFixed(1)) + 'K'
  }
  return formatted
}

export function formatUnshortenedNumber(unformatted: number | string | undefined | null, decimal: boolean = false, fullName: boolean = false) {
  return formatNumber(unformatted, false, decimal, false, fullName)
}

export function formatNumberSemiTruncate(unformatted: number | string | undefined | null, decimal: boolean = false, fullName: boolean = false) {
  return formatNumber(unformatted, true, decimal, false, fullName)
}

export function formatNumberFullTruncate(unformatted: number | string | undefined | null, decimal: boolean = false, fullName: boolean = false, floorAboveThreshold: boolean = false) {
  return formatNumber(unformatted, true, decimal, true, fullName, floorAboveThreshold)
}

export function randomFloatFromInterval(min: number, max: number) {
  return parseFloat((Math.random() * (max - min) + min).toFixed(4))
}

export const middleTruncate = (input: string, length = 12) => {
  if (!input) return ''
  const separator = '...'

  if (input.length <= length) return input

  const sepLen = separator.length,
    charsToShow = length - sepLen,
    frontChars = Math.ceil(charsToShow / 2),
    backChars = Math.floor(charsToShow / 2)

  return input.substring(0, frontChars) +
    separator +
    input.substring(input.length - backChars)
}

// VALIDATION FOR WALLET ADDRESS
function base64ToBytes(base64: string): Uint8Array {
  const binaryStr = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
  const len = binaryStr.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryStr.charCodeAt(i)
  }
  return bytes
}

// CRC-16-IBM checksum function
function crc16(data: Uint8Array): number {
  let crc = 0xFFFF
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i] << 8
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = (crc << 1) ^ 0x1021
      } else {
        crc <<= 1
      }
    }
  }
  return crc & 0xFFFF
}

// Function to validate TON wallet address
export function validateTonAddress(address: string): boolean {
  try {
    // Check if the address is Base64 encoded
    if (!/^[A-Za-z0-9_-]{48}$/.test(address)) {
      return false
    }

    // Decode Base64
    const decoded = base64ToBytes(address)

    // Check length of decoded address (must be 36 bytes)
    if (decoded.length !== 36) {
      return false
    }

    // Extract the payload (first 34 bytes) and the checksum (last 2 bytes)
    const payload = decoded.slice(0, 34)
    const checksum = (decoded[34] << 8) | decoded[35]

    // Calculate checksum from the payload
    const calculatedChecksum = crc16(payload)

    // Check if checksum matches
    return calculatedChecksum === checksum
  } catch (error) {
    console.error('Error validating address:', error)
    return false
  }
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function toOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

export function roundDecimals(num: number, precision: number) {
  const factor = Math.pow(10, precision)
  return Math.round(num * factor) / factor
}
